import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { XCircle, RefreshCw, Home, ArrowLeft, AlertTriangle } from 'lucide-react';
import { getPaymentInfo } from '../services/mercadopago';
import copy from '../copy';

interface PaymentInfo {
  id: string;
  status: string;
  status_detail: string;
  external_reference: string;
  amount: number;
  currency: string;
  payment_method: string;
  created_at: string;
  failure_reason?: string;
}

const PaymentFailure: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const paymentId = searchParams.get('payment_id');

  const formatPrice = (price: number) => `${copy.comun_simbolo}${price} ${copy.comun_moneda}`;

  useEffect(() => {
    const fetchPaymentInfo = async () => {
      if (!paymentId) {
        setError('ID de pago no encontrado');
        setLoading(false);
        return;
      }

      try {
        const payment = await getPaymentInfo(paymentId);
        setPaymentInfo({
          id: payment.id,
          status: payment.status,
          status_detail: payment.status_detail,
          external_reference: payment.external_reference,
          amount: payment.transaction_amount,
          currency: payment.currency_id,
          payment_method: payment.payment_method?.type || 'Desconocido',
          created_at: payment.date_created,
          failure_reason: payment.status_detail,
        });
      } catch (err) {
        console.error('Error fetching payment info:', err);
        setError('Error al cargar la información del pago');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentInfo();
  }, [paymentId]);

  const getFailureMessage = (statusDetail: string) => {
    const messages: { [key: string]: string } = {
      'cc_rejected_insufficient_amount': 'Fondos insuficientes en la tarjeta',
      'cc_rejected_bad_filled_card_number': 'Número de tarjeta incorrecto',
      'cc_rejected_bad_filled_date': 'Fecha de vencimiento incorrecta',
      'cc_rejected_bad_filled_security_code': 'Código de seguridad incorrecto',
      'cc_rejected_bad_filled_other': 'Datos de tarjeta incorrectos',
      'cc_rejected_blacklist': 'Tarjeta no autorizada',
      'cc_rejected_call_for_authorize': 'Autorización requerida del banco',
      'cc_rejected_card_disabled': 'Tarjeta deshabilitada',
      'cc_rejected_duplicated_payment': 'Pago duplicado',
      'cc_rejected_high_risk': 'Pago rechazado por riesgo',
      'cc_rejected_max_attempts': 'Máximo de intentos alcanzado',
      'cc_rejected_other_reason': 'Pago rechazado por el banco',
    };

    return messages[statusDetail] || 'El pago fue rechazado. Por favor intenta con otro método de pago.';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">Verificando Pago...</h1>
          <p className="text-primary-600">Por favor espera mientras verificamos el estado del pago</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">Error al Verificar Pago</h1>
          <p className="text-primary-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Intentar Nuevamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 section-padding">
      <div className="container-max">
        <div className="max-w-4xl mx-auto">
          {/* Failure Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary-900 mb-4">
              {copy.payment_failure_titulo}
            </h1>
            <p className="text-xl text-primary-600 max-w-2xl mx-auto">
              {copy.payment_failure_desc}. No te preocupes, puedes intentar nuevamente.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Payment Details */}
            {paymentInfo && (
              <div className="card-minimal">
                <h2 className="text-xl font-semibold text-primary-900 mb-6">
                  Detalles del Pago
                </h2>
                
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-primary-600">ID de Pago:</span>
                    <span className="font-medium text-primary-900">{paymentInfo.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Monto:</span>
                    <span className="font-semibold text-primary-900">
                      {formatPrice(paymentInfo.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Método de Pago:</span>
                    <span className="font-medium text-primary-900 capitalize">
                      {paymentInfo.payment_method}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Estado:</span>
                    <span className="font-medium text-red-600 capitalize">
                      Rechazado
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Fecha:</span>
                    <span className="font-medium text-primary-900">
                      {new Date(paymentInfo.created_at).toLocaleDateString('es-MX')}
                    </span>
                  </div>
                </div>

                {/* Failure Reason */}
                {paymentInfo.failure_reason && (
                  <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-medium text-red-800">Motivo del Rechazo</h3>
                        <p className="text-sm text-red-700 mt-1">
                          {getFailureMessage(paymentInfo.failure_reason)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* What to do next */}
            <div className="card-minimal">
              <h2 className="text-xl font-semibold text-primary-900 mb-6">
                ¿Qué puedes hacer?
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary-900">Intentar Nuevamente</h3>
                    <p className="text-sm text-primary-600">
                      Verifica los datos de tu tarjeta e intenta realizar el pago nuevamente
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <XCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary-900">Usar Otro Método</h3>
                    <p className="text-sm text-primary-600">
                      Prueba con una tarjeta diferente o otro método de pago disponible
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <AlertTriangle className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary-900">Contactar al Banco</h3>
                    <p className="text-sm text-primary-600">
                      Si el problema persiste, contacta a tu banco para verificar el estado de tu tarjeta
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="text-center mt-12 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/carrito" className="btn-primary inline-flex items-center">
                <RefreshCw className="mr-2 w-5 h-5" />
                Intentar Nuevamente
              </Link>
              <Link to="/cursos" className="btn-secondary inline-flex items-center">
                <ArrowLeft className="mr-2 w-5 h-5" />
                Volver a Cursos
              </Link>
              <Link to="/" className="btn-outline inline-flex items-center">
                <Home className="mr-2 w-5 h-5" />
                Ir al Inicio
              </Link>
            </div>
            
            <p className="text-sm text-primary-500">
              ¿Necesitas ayuda? Contacta a nuestro equipo de soporte en{' '}
              <a href="mailto:support@iberotech.org" className="text-accent-600 hover:underline">
                support@iberotech.org
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailure; 